import {Box, Label, type LabelProps, merge, Link} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {testIdProps} from '@github-ui/test-id-props'
import {useEffect} from 'react'
import {useListItemLeadingBadge} from './LeadingBadgeContext'

export type LeadingBadgeProps = Pick<LabelProps, 'variant' | 'size'> & {
  /**
   * Text that is shown as a visible Primer Label and as visually hidden text for screen readers.
   */
  title: string

  /**
   * Styling for the div surrounding the label
   */
  containerSx?: BetterSystemStyleObject

  /**
   * The link that will be opened when the item is clicked
   */
  href?: string
}

const defaultContainerSx: BetterSystemStyleObject = {display: 'inline-block', mr: 2}

export const LeadingBadge = ({title, containerSx, href, ...props}: LeadingBadgeProps) => {
  const {setLeadingBadge} = useListItemLeadingBadge()
  useEffect(() => setLeadingBadge(title), [setLeadingBadge, title])

  const content = (
    <>
      <Label
        sx={{overflow: 'hidden', display: 'grid'}}
        {...testIdProps('listview-item-leading-badge-label')}
        {...props}
      >
        <Box as="span" sx={{textOverflow: 'ellipsis', overflow: 'hidden', lineHeight: 'normal'}}>
          {title}
        </Box>
      </Label>
      <span className="sr-only">: </span>
    </>
  )

  const sx = containerSx ? merge<BetterSystemStyleObject>(defaultContainerSx, containerSx) : defaultContainerSx

  if (href) {
    return (
      <Link
        href={href}
        {...testIdProps('listview-item-leading-badge')}
        sx={{...sx, ':hover': {textDecoration: 'none'}}}
      >
        {content}
      </Link>
    )
  } else {
    return (
      <Box {...testIdProps('listview-item-leading-badge')} sx={sx}>
        {content}
      </Box>
    )
  }
}

try{ LeadingBadge.displayName ||= 'LeadingBadge' } catch {}